
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { DateTime, Duration } from 'luxon'

import { TimeStep } from '@/model/TimeStep'

import DateTimeAxis from './DateTimeAxis.vue'
import Heatline from '@/components/timeline/Heatline.vue'
import Slider from '@/components/timeline/Slider.vue'
import { ISnapshotLike } from '@/model/ISnapshotLike'
import { Representation, RepresentationTypes } from '@/model/Representation'

@Component({
  components: {
    Slider,
    DateTimeAxis,
    Heatline,
  },
})
export default class Timeline extends Vue {
  @Prop() mouseMoveEvt!: MouseEvent
  @Prop() mouseClickEvt!: MouseEvent

  @Prop({ required: true }) snapshot!: ISnapshotLike<TimeStep>
  @Prop({ required: true }) timeStep!: TimeStep
  @Prop({ required: true }) representation!: Representation<RepresentationTypes>
  @Prop({ type: Boolean, default: true }) activatedSlider!: boolean
  @Prop({ type: Boolean, default: true }) activatedAxis!: boolean
  @Prop({ type: Boolean, default: false }) playModeActivated!: boolean

  private current: TimeStep | null = null
  private loading = true

  get durationBefore(): Duration {
    return this.snapshot.getDurationBefore()
  }

  get durationAfter(): Duration {
    return this.snapshot.getDurationAfter()
  }

  get start(): DateTime {
    return this.snapshot.getStart()
  }

  get end(): DateTime {
    return this.snapshot.getEnd()
  }

  async mounted(): Promise<void> {
    this.loadSnapshot()
  }

  @Watch('snapshot')
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  onCurrentSnapshotChanged(snapshot: ISnapshotLike<TimeStep> | null): void {
    this.loading = true
    this.loadSnapshot()
  }

  loadSnapshot(): void {
    if (!this.snapshot) {
      return
    }

    // Only check city match - timestamp comparison is unreliable with FilteredSnapshot
    // because current.snapshot.timestamp refers to the base snapshot (unaligned)
    // while this.snapshot.timestamp may be aligned for cumulative rainfall
    if (this.current === null || this.current.snapshot.city !== this.snapshot.city) {
      this.current = this.snapshot.getReference()
    }

    this.loading = false
  }

  @Watch('timeStep')
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  onCurrentTimeStepChanged(timeStep: TimeStep | null): void {
    if (timeStep === null) return

    // Only check city match - timestamp comparison is unreliable with FilteredSnapshot
    // because timeStep.snapshot.timestamp refers to the base snapshot (unaligned)
    // while this.snapshot.timestamp may be aligned for cumulative rainfall
    if (this.snapshot && this.snapshot.city === timeStep.snapshot.city) {
      this.current = timeStep
      this.loading = false
    }
  }

  onDateTimeChanged(oldDateTime: DateTime, newDateTime: DateTime): void {
    if (!oldDateTime.equals(newDateTime)) {
      this.$emit('selected-snapshot-changed', newDateTime.toUTC())
    }
  }

  onTimeStepChanged(ts: TimeStep): void {
    if (ts !== this.timeStep) {
      this.$emit('selected-timestep-changed', ts)
    }
  }
}
